import moment from "moment";

import { ECHEQ_BATCH_DETAIL_RETURN_STATE_KEY } from "util/v3ShellLayoutSync";

import {
    BATCH_STATUS,
    ECHEQS_MASS_MANAGEMENT_LIST_PATH,
    resolveManagementActionI18nKey,
} from "./echeqMassManageConstants";
import {
    CARD_LABEL_PREFIX,
    getCardStatusLabel,
    mapBatchStatusToCardStatus,
    sumAmounts,
} from "./echeqMassManageListUtils";

const {
    FINISHED_SUCCESS: BATCH_STATUS_FINISHED_SUCCESS,
    FINISHED_ERROR: BATCH_STATUS_FINISHED_ERROR,
    PROCESSING: BATCH_STATUS_PROCESSING,
    FAILED: BATCH_STATUS_FAILED,
} = BATCH_STATUS;

const DETAIL_ITEM_STATUSES = new Set(["FINALIZADO", "FALLIDO", "PENDING"]);

const formatBatchProcessDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : String(value);
};

const resolveDetailRowId = (row) => String(row.idEcheq ?? row.echeqId ?? row.echeqID ?? "").trim();

const hasPositiveAmount = (value) => {
    if (value == null || value === "") {
        return false;
    }
    const parsed = Number.parseFloat(String(value));
    return !Number.isNaN(parsed) && parsed > 0;
};

/** API batch status → header badge status consumed by UI Kit. */
export const mapBatchApiStatusToHeaderStatus = (apiStatus) => {
    if (apiStatus === BATCH_STATUS_FINISHED_ERROR) {
        return "PROCESS_ERROR";
    }
    if (apiStatus === BATCH_STATUS_FINISHED_SUCCESS) {
        return "PROCESS_SUCCESS";
    }
    if (apiStatus === BATCH_STATUS_PROCESSING) {
        return "PROCESSING";
    }
    if (apiStatus === BATCH_STATUS_FAILED) {
        return "FAILED";
    }
    return apiStatus;
};

export const mapManagementActionToUi = (action) => (action === "PAY" ? "pay" : "admit");

export const mapBatchHeaderToStatusTag = (batch, resolveLabel) => {
    if (!batch?.status) {
        return undefined;
    }

    const cardStatus = mapBatchStatusToCardStatus(batch.status);

    return {
        label: resolveLabel(getCardStatusLabel(cardStatus)),
        status: mapBatchApiStatusToHeaderStatus(batch.status),
    };
};

export const mapBatchHeaderToGeneralData = (batch, resolveLabel) => {
    const safeBatch = batch ?? {};
    const actionKey = resolveManagementActionI18nKey(safeBatch.action);

    return {
        action: actionKey ? resolveLabel(actionKey) : safeBatch.action || "",
        reference: safeBatch.reference || "",
        processDate: formatBatchProcessDate(safeBatch.processDate),
        echeqsCount: safeBatch.quantity ?? 0,
        totalAmountArs: sumAmounts(safeBatch.arsAmountSuccessed, safeBatch.arsAmountFailed) ?? 0,
        totalAmountUsd: sumAmounts(safeBatch.usdAmountSuccessed, safeBatch.usdAmountFailed) ?? 0,
    };
};

const EMPTY_BATCH_SUMMARY = { byCurrency: {} };

const hasCurrencyActivity = (successAmount, failedAmount, successCount, failedCount) =>
    hasPositiveAmount(successAmount) || hasPositiveAmount(failedAmount) || successCount > 0 || failedCount > 0;

const buildCurrencyOutcome = (successCount, failedCount, successAmount, failedAmount) => ({
    successCount,
    successAmount: successAmount ?? 0,
    failedCount,
    failedAmount: failedAmount ?? 0,
});

export const mapBatchHeaderToSummary = (batch) => {
    const safeBatch = batch ?? {};
    const byCurrency = {};
    const arsSuccessCount = safeBatch.arsSuccessCount ?? 0;
    const arsFailedCount = safeBatch.arsFailedCount ?? 0;
    const usdSuccessCount = safeBatch.usdSuccessCount ?? 0;
    const usdFailedCount = safeBatch.usdFailedCount ?? 0;
    const hasArs = hasCurrencyActivity(
        safeBatch.arsAmountSuccessed,
        safeBatch.arsAmountFailed,
        arsSuccessCount,
        arsFailedCount,
    );
    const hasUsd = hasCurrencyActivity(
        safeBatch.usdAmountSuccessed,
        safeBatch.usdAmountFailed,
        usdSuccessCount,
        usdFailedCount,
    );

    if (hasArs) {
        byCurrency.ars = buildCurrencyOutcome(
            arsSuccessCount,
            arsFailedCount,
            safeBatch.arsAmountSuccessed,
            safeBatch.arsAmountFailed,
        );
    }

    if (hasUsd) {
        byCurrency.usd = buildCurrencyOutcome(
            usdSuccessCount,
            usdFailedCount,
            safeBatch.usdAmountSuccessed,
            safeBatch.usdAmountFailed,
        );
    }

    if (!hasArs && !hasUsd) {
        byCurrency.ars = buildCurrencyOutcome(arsSuccessCount, arsFailedCount, 0, 0);
    }

    return Object.keys(byCurrency).length > 0 ? { byCurrency } : EMPTY_BATCH_SUMMARY;
};

const mapDetailRowStatus = (row) => {
    const rawStatus = String(row.displayStatus ?? row.status ?? "")
        .trim()
        .toUpperCase();
    return DETAIL_ITEM_STATUSES.has(rawStatus) ? rawStatus : "FALLIDO";
};

/** Maps {@code mass.echeq.manage.detail} BATCH rows to UI Kit card items. */
export const mapBatchDetailRows = (detailList = []) =>
    (Array.isArray(detailList) ? detailList : [])
        .map((row) => {
            const idEcheq = resolveDetailRowId(row);
            if (!idEcheq) {
                return null;
            }

            const status = mapDetailRowStatus(row);

            if (row.simpleCard) {
                return { idEcheq, simpleCard: true, status };
            }

            return {
                idEcheq,
                simpleCard: false,
                status,
                creationDate: row.creationDate,
                paymentDate: row.paymentDate,
                number: row.number,
                character: row.character,
                amount: row.amount != null ? Number(row.amount) : undefined,
                currency: row.currency,
                recibedBy: row.recibedBy,
                recibedDocument: row.recibedDocument,
                ...(row.idTransaction ? { idTransaction: row.idTransaction } : {}),
                ...(row.errorMessage ? { errorMessage: row.errorMessage } : {}),
            };
        })
        .filter(Boolean);

export const buildMassEcheqManageBatchDetailPath = (idBatch) =>
    `${ECHEQS_MASS_MANAGEMENT_LIST_PATH}/${encodeURIComponent(idBatch)}/detail`;

export const buildMassEcheqManageBatchDetailLocation = (idBatch, returnPathname) => {
    const pathname = buildMassEcheqManageBatchDetailPath(idBatch);
    if (typeof returnPathname === "string" && returnPathname.startsWith("/")) {
        return {
            pathname,
            state: {
                [ECHEQ_BATCH_DETAIL_RETURN_STATE_KEY]: returnPathname,
            },
        };
    }
    return { pathname };
};

export const resolveMassEcheqManageBatchDetailTitle = (idBatch, resolveLabel) =>
    `${resolveLabel(`${CARD_LABEL_PREFIX}.batchId`)}: ${idBatch}`;
